import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetMyCaseLoadsVsEngagementParticipantsDetails } from "../../../../../api/services/program/programApis";
import {
  ITabs,
  MuiGrid,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
  resolveContent,
} from "../../../../../components";
import { PAGE_IDS } from "../../../../../helpers/constants";
import { getValueOrBlank } from "../../../../../helpers/utils";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import { myCaseloadsSiteCoreKeys } from "../../../sitecore/SiteCoreKeys";
import { MyCaseLoadsEngagementsResults } from "./components/myCaseLoadsVsEngagementsResults/MyCaseLoadsVsEngagementsResults";
import { MyCaseLoadsVsEngagementsWrapper } from "./MyCaseLoadsVsEngagements.style";

export interface MyCaseLoadsVsEngagementsProps {
  open: boolean;
  onClose: () => void;
}
const pageId = PAGE_IDS.COACH_PORTAL_DASHBOARD_MY_CASELOADS_VS_ENGAGEMENTS;

export const MyCaseLoadsVsEngagements: FC<MyCaseLoadsVsEngagementsProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { useFetchLabel, commonLabels } = useSiteCore();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { loggedInUser } = useGetLoggedInUserDetails();

  const [
    assignedData,
    notRegisteredData,
    notLoggedData,
    noCoachingReceivedData,
  ] = useGetMyCaseLoadsVsEngagementParticipantsDetails({
    loggedInCoachId: getValueOrBlank(loggedInUser?.id),
  });

  useEffect(() => {
    return () => {
      dispatch(
        toggleLoader({
          CPDashboardCaseLoadsVsEngagements: {
            isVisible: false,
          },
        }),
      );
    };
  }, []);

  useEffect(() => {
    if (
      MY_CASELOADS_AND_ENGAGEMENTS_TABS[selectedTabIndex].id ===
      tabIdAssignedTab
    ) {
      dispatch(
        toggleLoader({
          isMyCaseLoadsPending: {
            isVisible: assignedData.isFetching,
            message: commonLabels.loadingPleaseWait,
          },
        }),
      );
    }
  }, [selectedTabIndex, assignedData.isFetching]);

  useEffect(() => {
    if (
      MY_CASELOADS_AND_ENGAGEMENTS_TABS[selectedTabIndex].id ===
      tabIdNotRegistered
    ) {
      dispatch(
        toggleLoader({
          isMyCaseLoadsPending: {
            isVisible: notRegisteredData.isFetching,
            message: commonLabels.loadingPleaseWait,
          },
        }),
      );
    }
  }, [selectedTabIndex, notRegisteredData.isFetching]);

  useEffect(() => {
    if (
      MY_CASELOADS_AND_ENGAGEMENTS_TABS[selectedTabIndex].id === tabIdNotLogged
    ) {
      dispatch(
        toggleLoader({
          isMyCaseLoadsPending: {
            isVisible: notLoggedData.isFetching,
            message: commonLabels.loadingPleaseWait,
          },
        }),
      );
    }
  }, [selectedTabIndex, notLoggedData.isFetching]);

  useEffect(() => {
    if (
      MY_CASELOADS_AND_ENGAGEMENTS_TABS[selectedTabIndex].id === tabIdNoCoaching
    ) {
      dispatch(
        toggleLoader({
          isMyCaseLoadsPending: {
            isVisible: noCoachingReceivedData.isFetching,
            message: commonLabels.loadingPleaseWait,
          },
        }),
      );
    }
  }, [selectedTabIndex, noCoachingReceivedData.isFetching]);

  useEffect(() => {
    if (
      assignedData.isError ||
      notRegisteredData.isError ||
      notLoggedData.isError ||
      noCoachingReceivedData.isError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [
    assignedData.isError,
    notRegisteredData.isError,
    notLoggedData.isError,
    noCoachingReceivedData.isError,
  ]);

  const {
    myCaseloadsTitle,
    myCaseloadsVsEngagementPopupHeader,
    assignedTab,
    notRegisteredTab,
    noCoachingTab,
    notLoggedTab,
    noAssignedCaseloads,
    noParticipantsWithRegistrationPending,
    noParticipantsWithCoachingPendingForOver30Days,
    noParticipantsWithLoginPendingForOver30Days,
  } = myCaseloadsSiteCoreKeys;

  const getLabel = useFetchLabel(myCaseloadsTitle);

  const renderAssignedTabContent = useMemo(
    () => (
      <MyCaseLoadsEngagementsResults
        rows={assignedData?.data ?? []}
        emptyMessageKey={noAssignedCaseloads}
      />
    ),
    [assignedData?.data],
  );

  const renderNotRegisteredTabContent = useMemo(
    () => (
      <MyCaseLoadsEngagementsResults
        rows={notRegisteredData?.data ?? []}
        emptyMessageKey={noParticipantsWithRegistrationPending}
      />
    ),
    [notRegisteredData?.data],
  );

  const renderNoCoachingTabContent = useMemo(
    () => (
      <MyCaseLoadsEngagementsResults
        rows={noCoachingReceivedData?.data ?? []}
        emptyMessageKey={noParticipantsWithCoachingPendingForOver30Days}
      />
    ),
    [noCoachingReceivedData?.data],
  );

  const renderNotLoggedTabContent = useMemo(
    () => (
      <MyCaseLoadsEngagementsResults
        rows={notLoggedData?.data ?? []}
        emptyMessageKey={noParticipantsWithLoginPendingForOver30Days}
      />
    ),
    [notLoggedData?.data],
  );

  const assignedCount = Number(assignedData?.data?.length ?? 0);
  const notRegisteredCount = Number(notRegisteredData?.data?.length ?? 0);
  const notLoggedCount = Number(notLoggedData?.data?.length ?? 0);
  const noCoachingReceivedCount = Number(
    noCoachingReceivedData?.data?.length ?? 0,
  );

  const tabIdAssignedTab = `${getLabel(assignedTab)}-tab`;
  const tabIdNotRegistered = `${getLabel(notRegisteredTab)}-tab`;
  const tabIdNoCoaching = `${getLabel(noCoachingTab)}-tab`;
  const tabIdNotLogged = `${getLabel(notLoggedTab)}-tab`;

  const MY_CASELOADS_AND_ENGAGEMENTS_TABS: ITabs[] = [
    {
      id: tabIdAssignedTab,
      label: getLabel(assignedTab),
      count: assignedCount.toString(),
      content: () => renderAssignedTabContent,
    },
    {
      id: tabIdNotRegistered,
      label: getLabel(notRegisteredTab),
      count: notRegisteredCount.toString(),
      content: () => renderNotRegisteredTabContent,
    },
    {
      id: tabIdNoCoaching,
      label: getLabel(noCoachingTab),
      count: noCoachingReceivedCount.toString(),
      content: () => renderNoCoachingTabContent,
    },
    {
      id: tabIdNotLogged,
      label: getLabel(notLoggedTab),
      count: notLoggedCount.toString(),
      content: () => renderNotLoggedTabContent,
    },
  ];

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTabIndex(value);
  };
  const currentTabContent = resolveContent(
    MY_CASELOADS_AND_ENGAGEMENTS_TABS[selectedTabIndex]?.content,
  );

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={getLabel(myCaseloadsVsEngagementPopupHeader)}
      hasLargeContent
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      onSecondaryButtonClick={onClose}
    >
      <MyCaseLoadsVsEngagementsWrapper>
        <MuiGrid container>
          <MuiGrid container>
            <MuiGrid size={{ xs: 12 }} className="header-right">
              <MuiTabs
                tabs={MY_CASELOADS_AND_ENGAGEMENTS_TABS}
                value={selectedTabIndex}
                onChange={onTabChange}
                tabsTestIds={MY_CASELOADS_AND_ENGAGEMENTS_TABS.map(
                  (_, index) => `${pageId}-tab${index + 1}`,
                )}
                className="program-participant-tab"
              >
                <MuiTabPanel
                  panelId={`${pageId}-tab${selectedTabIndex + 1}`}
                  value={selectedTabIndex}
                  index={selectedTabIndex}
                  isPanelWithMount
                  ariaLabelledById={
                    MY_CASELOADS_AND_ENGAGEMENTS_TABS[selectedTabIndex]?.id
                  }
                  data-testid="tab-panel-assigned"
                >
                  {currentTabContent}
                </MuiTabPanel>
              </MuiTabs>
            </MuiGrid>
          </MuiGrid>
        </MuiGrid>
      </MyCaseLoadsVsEngagementsWrapper>
    </MuiModal>
  );
};
